import type { JSX } from 'react';
import { Link, useParams } from 'react-router-dom';
import { aggregateMastery } from '@yuan-math/learning';
import { Card, MasteryBar } from '@yuan-math/ui';
import { registry, useLearner } from '../state/LearnerContext.tsx';
import { PathMap } from '../components/PathMap.tsx';
import { NotFoundPage } from './NotFoundPage.tsx';

/**
 * One course: its units, its lessons, and the student's standing in each.
 *
 * The units are drawn as a path rather than listed, because a course is a walk
 * through the graph and a list of rows says nothing about that. `PathMap`
 * carries the rules; this page only supplies the units.
 */
export function CoursePage(): JSX.Element {
  const { courseId } = useParams();
  const { profile } = useLearner();

  const course = courseId ? registry.course(courseId) : undefined;
  if (!course) return <NotFoundPage />;

  const units = registry.unitsOfCourse(course.id);
  const concepts = registry.conceptsOfCourse(course.id);
  const mastery = profile ? aggregateMastery(concepts.map((c) => c.id), profile.concepts, Date.now()) : 0;

  return (
    <div className="page page--wide">
      <nav className="breadcrumb" aria-label="Breadcrumb">
        <Link to="/courses">Courses</Link>
        <span aria-hidden="true">/</span>
        <span>{course.title}</span>
      </nav>

      <header className="page-header">
        <h1 className="page__title">{course.title}</h1>
        <p className="page__lead">{course.description}</p>
        {profile ? <MasteryBar value={mastery} label="Course mastery" size="lg" /> : null}
      </header>

      {units.length === 0 ? (
        <Card>
          <p>This course is announced but not yet written. Its concepts are already on the Math Map.</p>
        </Card>
      ) : (
        <PathMap units={units} />
      )}

    </div>
  );
}
